import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GameController, Warning, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link - no token provided');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/auth'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0047FF] p-4">
              <GameController size={48} weight="fill" color="#FFFFFF" />
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#09090B] mb-2">
            Reset Password
          </h1>
          <p className="text-[#71717A] text-sm">Set your new password</p>
        </div>

        {success ? (
          <div className="p-6 bg-green-50 border border-green-200 text-center" data-testid="reset-success">
            <CheckCircle size={48} weight="fill" className="mx-auto text-green-600 mb-3" />
            <h3 className="font-heading text-lg font-bold text-green-800 mb-1">Password Reset!</h3>
            <p className="text-sm text-green-700">Redirecting to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">
                <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="new-password-input"
                className="border-[#E4E4E7]"
                disabled={!token}
              />
              {password && password.length < 8 && (
                <p className="text-xs text-[#71717A]">{password.length}/8 characters</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Repeat your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
                className="border-[#E4E4E7]"
                disabled={!token}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white"
              disabled={loading || !token}
              data-testid="submit-reset-button"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/auth')}
              className="w-full text-[#71717A]"
            >
              Back to Login
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
